<template>
  <v-card elevation="0" class="pa-0 h-100 grayedBackground">
    <HeaderModule title="My Account" width="1224px" icon="account">
      <template #staticContent>
        <v-card
          class="mx-auto pa-0 mt-5 grayedBackground"
          elevation="0"
          max-width="800px"
        >
          <!-- Account details -->
          <v-card class="border-thin" elevation="0">
            <!-- Header -->
            <v-card-title class="cardTitle pa-0 pr-4">
              <v-card-actions>
                <v-icon icon="mdi-account-edit" class="ma-2" size="24" />
                <div class="cardTitle">Account details</div>
              </v-card-actions>
            </v-card-title>

            <v-card-text class="border-t-thin pa-0">
              <v-row class="text-left ma-0">
                <v-col cols="8">
                  <v-text-field
                    density="compact"
                    rounded="0"
                    variant="outlined"
                    class="fields"
                    v-model="user_object.name"
                    :rules="rules.name"
                    label="Name"
                    autocomplete="off"
                  ></v-text-field>
                  <v-btn
                    color="success"
                    variant="elevated"
                    @click="
                      action = 1;
                      dialog = true;
                    "
                    aria-label="Update name"
                    :disabled="user_object?.name === userProfile?.name"
                    rounded="0"
                  >
                    Update name
                  </v-btn>
                  <br />
                  <v-btn
                    class="mt-4"
                    color="info"
                    variant="elevated"
                    @click="
                      dialog = true;
                      action = 2;
                    "
                    aria-label="reset password"
                    rounded="0"
                  >
                    change password
                  </v-btn>
                </v-col>
                <v-divider vertical />
                <v-col cols="4" class="grayedText">
                  <v-card-actions class="px-0 py-2 align-start">
                    <v-icon icon="mdi-email-outline" class="mt-2 mr-1" />
                    <v-card-text class="pa-0">
                      <div>
                        <b>Email</b>
                      </div>
                      <div style="word-break: break-word">
                        {{ userProfile?.email }}
                      </div>
                    </v-card-text>
                  </v-card-actions>

                  <v-card-actions class="px-0 py-2 align-start">
                    <v-icon icon="mdi-calendar" class="mt-2 mr-1" />
                    <v-card-text class="pa-0">
                      <div>
                        <b>User since</b>
                      </div>
                      <div>
                        <!-- Convert date into human language -->
                        {{
                          new Date(userProfile?.created_at).toLocaleDateString(
                            "en-GB",
                            { day: "numeric", month: "long", year: "numeric" },
                          )
                        }}
                      </div>
                    </v-card-text>
                  </v-card-actions>

                  <v-card-actions class="px-0 py-2 align-start">
                    <v-icon icon="mdi-briefcase-outline" class="mt-2 mr-1" />
                    <v-card-text class="pa-0">
                      <div>
                        <b>Role</b>
                        <v-tooltip>
                          <template #default>
                            <div style="text-align: center">
                              Please reach out to one of your group
                              administrators<br />
                              if you need to update your role or account email
                            </div>
                          </template>
                          <template v-slot:activator="{ props }">
                            <v-icon
                              v-bind="props"
                              class="ml-1"
                              icon="mdi-help-circle"
                              size="x-small"
                            />
                          </template>
                        </v-tooltip>
                      </div>
                      <div>{{ role }}</div>
                    </v-card-text>
                  </v-card-actions>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Email preferences -->
          <v-card class="border-thin mt-6" elevation="0">
            <v-card-title class="cardTitle pa-0 pr-4">
              <v-card-actions>
                <v-icon icon="mdi-email-outline" class="ma-2" size="24" />
                <div class="cardTitle">Email preferences</div>
              </v-card-actions>
            </v-card-title>
            <div v-if="fetchingEmailPreferences">
              <v-card-actions
                v-for="n in 5"
                :key="n"
                class="w-100 pa-0"
                style="height: 56px"
              >
                <v-skeleton-loader type="list-item-avatar" width="100%" />
              </v-card-actions>
            </div>
            <div v-else-if="nonePreferencesAvailable">
              <v-card-actions class="justify-center">
                <v-alert color="info" icon="mdi-information">
                  <div style="font-weight: 500" class="text-left">
                    Sorry, we're still building your account. Please return
                    later to set your email preferences.
                  </div>
                </v-alert>
              </v-card-actions>
            </div>
            <div v-else>
              <v-card-actions
                v-for="preference in emailPreferences"
                :key="preference.id"
                class="text-left border-t-thin ma-0"
                :class="{ preferenceClickable: !updatingEmailPreferences }"
                style="height: 56px"
                @click.prevent="
                  () => {
                    if (!updatingEmailPreferences) {
                      preference.value = !preference.value;
                    }
                  }
                "
              >
                <v-card-actions class="w-100 pa-0">
                  <v-checkbox
                    v-model="preference.value"
                    hide-details
                    :disabled="updatingEmailPreferences"
                    color="primary"
                  />
                  <v-card-text class="pl-0">
                    <div class="text-body-2">{{ preference.name }}</div>
                    <!-- Description. Isn't needed atm -->
                    <!-- <div class="text-caption">
                    {{ preference.description }}
                  </div> -->
                  </v-card-text>
                </v-card-actions>
              </v-card-actions>
            </div>

            <v-card-actions
              v-if="!nonePreferencesAvailable"
              class="pa-4 border-t-thin"
            >
              <!-- select/deselect all button -->
              <v-btn
                v-if="!fetchingEmailPreferences"
                color="primary"
                @click="handleToggleAllPreferences()"
                variant="text"
                :disabled="updatingEmailPreferences"
                rounded="0"
                >{{
                  areAllPreferencesSelected ? "deselect all" : "select all"
                }}</v-btn
              >
              <v-spacer />
              <!-- Update button -->
              <v-btn
                :disabled="fetchingEmailPreferences"
                :loading="updatingEmailPreferences"
                color="success"
                rounded="0"
                @click="updateEmailPreferences"
                variant="elevated"
                >update preferences</v-btn
              >
            </v-card-actions>
          </v-card>

          <!--  TODO: Delete account -->
          <!-- <v-card class="mt-6 pa-0" elevation="0">
            <v-card-actions class="pa-0">
              <v-btn
                color="error"
                variant="text"
                block
                class="font-weight-bold"
                style="height: 52px !important"
                >delete account</v-btn
              >
            </v-card-actions>
          </v-card> -->
        </v-card>
      </template>
    </HeaderModule>

    <v-dialog v-model="dialog" scrollable persistent max-width="500px">
      <v-card>
        <v-toolbar
          :color="this.$store.state.config.siteConfig.toolbar_colour"
          dark
          class="text-h6 text-center"
          max-height="64px"
        >
          <v-spacer>
            <v-toolbar-title
              >{{ action === 1 ? "Confirm your password" : "Reset password" }}
            </v-toolbar-title>
          </v-spacer>
        </v-toolbar>
        <v-form ref="form" lazy-validation>
          <v-container>
            <v-row>
              <v-card-text v-if="action === 1" class="text-center mt-4 pb-1">
                <v-text-field
                  :type="showPW ? 'text' : 'password'"
                  v-model="user_object.password"
                  :rules="rules.password"
                  :append-icon="showPW ? 'mdi-eye' : 'mdi-eye-off'"
                  label="Password"
                  required
                  density="compact"
                  rounded="0"
                  variant="outlined"
                  class="fields"
                  @click:append="showPW = !showPW"
                  @keyup.enter="submit()"
                  autocomplete="off"
                ></v-text-field>
              </v-card-text>
              <v-card-text
                v-else
                class="text-center mt-4 mb-4"
                style="font-size: 1.07em"
              >
                You will be sent an email with a link to reset your password.
                Are you sure you want to continue?
              </v-card-text>
            </v-row>
          </v-container>
        </v-form>
        <v-divider></v-divider>
        <v-card-actions v-if="action === 1">
          <v-btn
            color="success"
            variant="elevated"
            tile
            @click="
              dialog = false;
              user_object.password = '';
            "
            aria-label="cancel"
          >
            cancel
          </v-btn>
          <v-spacer />
          <v-btn
            color="warning"
            variant="elevated"
            tile
            @click="submit()"
            aria-label="confirm"
          >
            confirm
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-btn
            color="success"
            variant="elevated"
            rounded="0"
            @click="dialog = false"
            aria-label="cancel"
          >
            cancel
          </v-btn>
          <v-spacer />
          <v-btn
            color="info"
            variant="elevated"
            rounded="0"
            @click="resetPassword()"
            aria-label="reset"
            :disabled="requestNewPWSpinner"
            :loading="requestNewPWSpinner"
          >
            <template v-slot:loader>
              <span class="custom-loader">
                <v-icon light>mdi-cached</v-icon>
              </span>
            </template>
            Send Link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import HeaderModule from "@/components/HeaderModule.vue";
import { systemMessages } from "@/mixins/SystemMessages";

export default {
  name: "MyAccount",
  mixins: [systemMessages],
  data: () => ({
    user_object: {},
    requestNewPWSpinner: false,
    dialog: false,
    action: 0,
    showPW: false,
    emailPreferences: [],
    fetchingEmailPreferences: false,
    updatingEmailPreferences: false,
  }),
  components: {
    HeaderModule,
  },
  computed: {
    userProfile: {
      get() {
        return this.$store.getters.userProfile;
      },
      set(value) {
        this.$store.commit("setUserProfile", value);
      },
    },
    rules() {
      return {
        name: [
          (value) => !!value || "Required.",
          (value) =>
            (value && value.length <= 255) || "Maximum of 255 characters",
          (value) => (value && value.length >= 3) || "Minimum of 2 characters",
        ],
        password: [(value) => !!value || "password required"],
      };
    },
    role() {
      // values of the "permission_level". User gets the highest role assigned to them across all clients
      const roles = {
        1: "Group Admin OCSI Staff",
        2: "Group Admin",
        3: "Power User",
        4: "View Reports",
        5: "Public User",
      };
      return roles[this.$store.getters.permission_level] || "Unknown Role";
    },
    areAllPreferencesSelected() {
      return this.emailPreferences.every((p) => p.value === true);
    },
    nonePreferencesAvailable() {
      return (
        !this.$store.state.config.userProfile.synced ||
        (!this.fetchingEmailPreferences && !this.emailPreferences.length)
      );
    },
  },
  mounted() {
    this.updateForm();

    if (this.$store.state.config.userProfile.synced) {
      this.getEmailPreferences();
    }
  },
  methods: {
    submit() {
      // snackbar
      this.emit.emit("systemMessage", {
        title: "Updating your name",
        message: "Updating",
        timeout: 4000,
        colour: "warning",
      });

      // send the data to the API
      this.$axios
        .put("/update-user-details", this.user_object)
        .then((response) => {
          this.dialog = false;
          this.emit.emit("systemMessage", {
            title: "User profile updated",
            message: "Success!",
            timeout: 3000,
            colour: "success",
          });

          // update the data
          this.userProfile = response.data;

          // and update the form in the template
          this.updateForm();
        })
        .catch((error) => {
          this.emit.emit("systemMessage", {
            message: error.response.data.message,
            title: "Error updating user profile",
            timeout: -1,
            colour: "error",
          });
        })
        .finally(() => {
          // reset the dialog
          this.user_object.password = "";
        });
    },
    updateForm() {
      this.user_object = Object.assign({}, this.userProfile);
    },
    resetPassword() {
      this.requestNewPWSpinner = true;
      let resetPasswordEmail = {
        email: this.$store.state.config.userProfile.email,
      };
      // make the call!
      this.$axios
        .post("/logged-password-reset-request", resetPasswordEmail)
        .then(() => {
          this.emit.emit("systemMessage", {
            message:
              "Check email: " +
              resetPasswordEmail.email +
              " for password reset link",
            title: "Check email",
            timeout: -1,
            colour: "info",
          });
          this.requestNewPWSpinner = false;
        })
        .catch((error) => {
          this.emit.emit("systemMessage", {
            message: error.response.data.message,
            title: "Error! Password reset failed",
            timeout: -1,
            colour: "error",
          });
          this.requestNewPWSpinner = false;
        })
        .finally(() => {
          this.dialog = false;
        });
    },
    async getEmailPreferences() {
      try {
        this.fetchingEmailPreferences = true;
        const response = await this.$axios.get("/get-email-preferences");

        this.emailPreferences = response.data;
      } catch (error) {
        this.emit.emit("systemMessage", {
          message: error.response.data.message,
          title: "Failed! Couldn't fetch your email preferences",
          timeout: 5000,
          colour: "error",
        });
      } finally {
        this.fetchingEmailPreferences = false;
      }
    },
    async updateEmailPreferences() {
      try {
        this.updatingEmailPreferences = true;
        const response = await this.$axios.put(
          "/update-email-preferences",
          this.emailPreferences,
        );

        this.emailPreferences = response.data;

        this.successPill({
          title: "Email preferences successfully updated",
          showClose: false,
        });
      } catch (error) {
        this.emit.emit("systemMessage", {
          message: error.response.data.message,
          title: "Failed! Couldn't update your email preferences",
          timeout: 5000,
          colour: "error",
        });
      } finally {
        this.updatingEmailPreferences = false;
      }
    },
    handleToggleAllPreferences() {
      const newValue = !this.areAllPreferencesSelected;

      this.emailPreferences.forEach((p) => {
        p.value = newValue;
      });
    },
  },
};
</script>

<style scoped>
.preferenceClickable:hover {
  background-color: #bfe0f8;
  transition: background-color 0.1s ease;
  cursor: pointer;
}
</style>
